import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import AntPlusDeviceCombobox from "@/components/AntPlusDeviceCombobox";
import { useTranslation } from "@/i18n/LanguageContext";

const EMPTY_GENDER_VALUE = "__none";

export default function ClientDialog({ client, onClose, onSave }) {
  const t = useTranslation();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [birthDate, setBirthDate] = useState("");
  const [device, setDevice] = useState("");
  const [gender, setGender] = useState(EMPTY_GENDER_VALUE);
  const [weight, setWeight] = useState("");

  useEffect(() => {
    setName(client?.name || "");
    setEmail(client?.email || "");
    setBirthDate(client?.birthDate || "");
    setDevice(client?.device || "");
    setGender(client?.gender || EMPTY_GENDER_VALUE);
    setWeight(client?.weight != null ? String(client.weight) : "");
  }, [client]);

  if (!client) return null;

  const submit = (event) => {
    event.preventDefault();
    const trimmedDevice = device.trim();
    const trimmedWeight = weight.trim();
    const parsedWeight = trimmedWeight ? Number(trimmedWeight) : null;

    onSave({
      name: name.trim(),
      email: email.trim(),
      birthDate,
      ...(trimmedDevice ? { device: trimmedDevice } : {}),
      gender: gender === EMPTY_GENDER_VALUE ? null : gender,
      weight: Number.isFinite(parsedWeight) ? parsedWeight : null,
    });
  };

  return (
    <Dialog open={!!client} onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        className="border-border"
        data-testid="client-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">
            {client.id
              ? t("clients.dialog.detailTitle")
              : t("clients.dialog.createTitle")}
          </DialogTitle>
          <DialogDescription className="text-xs">
            {t("clients.dialog.description")}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label className="text-xs">{t("common.fields.name")}</Label>
            <Input
              value={name}
              onChange={(event) => setName(event.target.value)}
              required
              data-testid="client-dialog-name"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs">{t("common.fields.email")}</Label>
            <Input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              required
              data-testid="client-dialog-email"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs">{t("common.fields.birthDate")}</Label>
            <Input
              type="date"
              value={birthDate}
              onChange={(event) => setBirthDate(event.target.value)}
              required
              data-testid="client-dialog-birth-date"
            />
          </div>
          <div className="space-y-2">
            <Label className="text-xs">{t("common.fields.device")}</Label>
            <AntPlusDeviceCombobox
              value={device}
              onChange={setDevice}
              dropdownSide="top"
              labels={{
                placeholder: t("clients.dialog.devicePlaceholder"),
                searchPlaceholder: t("clients.dialog.deviceSearch"),
                noResults: t("clients.dialog.deviceNoResults"),
                notInList: t("clients.dialog.deviceNotInList"),
                customPlaceholder: t("clients.dialog.deviceCustomPlaceholder"),
              }}
              testId="client-dialog-device"
            />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label className="text-xs">{t("common.fields.gender")}</Label>
              <Select value={gender} onValueChange={setGender}>
                <SelectTrigger data-testid="client-dialog-gender">
                  <SelectValue
                    placeholder={t("clients.dialog.genderPlaceholder")}
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={EMPTY_GENDER_VALUE}>
                    {t("clients.dialog.genderPlaceholder")}
                  </SelectItem>
                  <SelectItem value="female">
                    {t("common.states.female")}
                  </SelectItem>
                  <SelectItem value="male">
                    {t("common.states.male")}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs">{t("common.fields.weight")}</Label>
              <Input
                type="number"
                inputMode="decimal"
                min="0.1"
                max="500"
                step="0.1"
                value={weight}
                onChange={(event) => setWeight(event.target.value)}
                placeholder={t("clients.dialog.weightPlaceholder")}
                data-testid="client-dialog-weight"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={onClose}
              className="rounded-lg"
              data-testid="client-dialog-cancel"
            >
              {t("common.actions.cancel")}
            </Button>
            <Button
              type="submit"
              className="rounded-lg"
              data-testid="client-dialog-save"
            >
              {client.id
                ? t("common.actions.saveChanges")
                : t("clients.dialog.createSubmit")}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
